import type { paths } from "../types/gen/services";
import config from "../../../config.json"
import { executeWithRetries, RetryStrategy } from "./retryUtils";

interface EndpointParameters {
  query?: Record<string, string | number | null | undefined>;
  path?: Record<string, string | number | null | undefined>;
}

const globalBaseUrl = config.ENDPOINT;

export function buildEndpoint(
  baseUrl: string,
  path: string,
  parameters: EndpointParameters
): string {
  let finalPath: string = path;
  if (parameters.path) {
    for (const [param, value] of Object.entries(parameters.path)) {
      if (value !== null && value !== undefined) {
        finalPath = finalPath.replace("{" + param + "}", value.toString());
      }
    }
  }

  const queryStr = parameters.query
    ? // @ts-expect-error It is ok to pass an object with null or undefined values to URLSearchParams
      "?" + new URLSearchParams(parameters.query).toString()
    : "";

  return baseUrl + finalPath + queryStr;
}

interface RetryArgs {
    maxRetries?: number;
    initialBackoffMs?: number;
    retryStrategy?: RetryStrategy;    
}

interface CallArgs<T> {
  baseUrl?: string;
  path: keyof paths;
  parameters: EndpointParameters;
  method: "GET" | "POST";
  token: string;
  body?: T;
  retryArgs?: RetryArgs;
}

export interface MethodOptions {
  baseUrl?: string;
}

export async function callAPIMethod<B>({
  baseUrl,
  path,
  parameters,
  method,
  token,
  body,
  retryArgs,
}: CallArgs<B>): Promise<unknown> {
  const endpoint = buildEndpoint(baseUrl || globalBaseUrl, path, parameters);
  const fetchArgs: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  };
  if (body) {
    fetchArgs.body = JSON.stringify(body);
  }

  const maxRetries = retryArgs?.maxRetries ?? 1;
  const initialBackoffMs = retryArgs?.initialBackoffMs ?? 1;
  const retryStrategy = retryArgs?.retryStrategy ?? RetryStrategy.CONSTANT;

  return await executeWithRetries(
    async () => {
        try {
            const response = await fetch(endpoint, fetchArgs);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status.toString()}`);
            }

            const result: unknown = await response.json();
            return result;
        } catch (error: unknown) {
            throw new Error(
                `API error: ${method} ${endpoint} ${(error as Error).message}`
            );
        }
    },
    maxRetries,
    initialBackoffMs,
    retryStrategy
  )
}

export function getGetAPIMethod<
  P extends keyof paths,
  R extends Extract<paths[P]["get"], { responses: object }>
>(
  path: P,
  options?: MethodOptions
): (
  {token, retryArgs, ...parameters}: { token: string, retryArgs?: RetryArgs } & R["parameters"]
) => Promise<R["responses"][200]["content"]["application/json"]> {
  const baseUrl = options?.baseUrl;
  return async ({ token, retryArgs, ...parameters }) =>
    callAPIMethod({ baseUrl, path, parameters, method: "GET", token, retryArgs });
}

export function getPostAPIMethod<
  P extends keyof paths,
  R extends Extract<
    paths[P]["post"],
    { requestBody: { content: { "application/json": object } } }
  >,
  B = R["requestBody"]["content"]["application/json"]
>(
  path: P,
  options?: MethodOptions
): (
  { token, body, retryArgs, ...parameters}: { token: string, body: B, retryArgs?: RetryArgs } & R["parameters"]
) => Promise<R["responses"][200]["content"]["application/json"]> {
  const baseUrl = options?.baseUrl;
  return async ({ token, body, retryArgs, ...parameters }) =>
    callAPIMethod({ baseUrl, path, parameters, method: "POST", token, body, retryArgs });
}
